import {
  AUTO_FILL_METHOD_ORDER,
  computeParkingTarget,
  EXTRA_PARKING_METHODS_ENABLED,
  PARKING_METHOD_KEYS,
} from './parking/constants';
import { recomputeParking as computeParkingResult } from './parkingEngine';
import { loadState, scheduleSave as persistScheduleSave } from './persistence';
import type { SunPosition } from './shadowCheck';
import { computeSunPosition } from './shadowCheck';
import type { SlopeCheckResult } from './slopeCheck';
import { computeSlopeCheck } from './slopeCheck';
import type { State } from './types';

// The live, mutable application state singleton, plus the "recompute and
// cache" orchestration that used to be module-level globals in index.html
// (slopeCheckCache, sunPositionCache, scheduleSave's debounce timer). Every
// UI module imports `state` from here rather than holding its own copy.

export const state: State = loadState();

let slopeCheckCache: Map<string, SlopeCheckResult> = new Map();
export function getSlopeCheckCache(): Map<string, SlopeCheckResult> {
  return slopeCheckCache;
}
export function recomputeSlopeCheck(): void {
  try {
    slopeCheckCache = computeSlopeCheck(state);
  } catch {
    slopeCheckCache = new Map();
  }
}

/**
 * Recomputes parking/bicycle layout via the de-globalized engine and applies the result
 * onto `state` in place. tandem/roadDirect have no manual on/off in the UI — every call
 * re-derives their `enabled` from scratch (off, then greedily turned on in priority
 * order until the provision target is met), so they track requiredCars/targetRatio and
 * the site layout automatically instead of being sticky user-set state.
 */
export function recomputeParking(): void {
  const runEngine = () =>
    computeParkingResult({
      site: state.site,
      buildings: state.buildings,
      parking: state.parking,
      bicycle: state.bicycle,
    });
  for (const key of AUTO_FILL_METHOD_ORDER) state.parking.methods[key].enabled = false;
  let result = runEngine();
  const target = computeParkingTarget(state.parking);
  if (target != null && EXTRA_PARKING_METHODS_ENABLED) {
    for (const key of AUTO_FILL_METHOD_ORDER) {
      if (result.capacity >= target) break;
      state.parking.methods[key].enabled = true;
      result = runEngine();
    }
  }
  state.parking.generatedSlots = result.generatedSlots;
  state.parking.lanes = result.lanes;
  state.parking.capacity = result.capacity;
  state.parking.drivewayConnectors = result.drivewayConnectors;
  state.parking.circulationWarning = result.circulationWarning;
  for (const key of PARKING_METHOD_KEYS) {
    Object.assign(state.parking.methods[key], result.methods[key]);
  }
  state.bicycle.zone = result.bicycle.zone;
  state.bicycle.capacity = result.bicycle.capacity;
}

export function scheduleSave(): void {
  persistScheduleSave(state);
}

let sunPositionCache: { key: string | null; positions: Map<number, SunPosition> | null } = {
  key: null,
  positions: null,
};
export function invalidateSunPositionCache(): void {
  sunPositionCache = { key: null, positions: null };
}
export function getSunPositions(): Map<number, SunPosition> {
  const s = state.shadowCheck;
  const origin = state.site.origin;
  if (!origin) return new Map();
  const key = `${origin.lat.toFixed(6)},${origin.lng.toFixed(6)},${s.date.month},${s.date.day},${s.hours.join(',')}`;
  if (sunPositionCache.key === key && sunPositionCache.positions) return sunPositionCache.positions;
  const positions = new Map<number, SunPosition>();
  s.hours.forEach((h) => {
    positions.set(h, computeSunPosition(origin.lat, origin.lng, s.date.month, s.date.day, h));
  });
  sunPositionCache = { key, positions };
  return positions;
}
